import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SocialShare — inline row of one-tap share buttons for WhatsApp, LinkedIn, Twitter/X + Copy Link.
 * Uses window.location.href by default. Pass `url`, `title`, `text` to override.
 */
const SocialShare = ({ url, title = '', text = '', size = 'sm', className = '', label = 'Share:' }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text || title);

  const links = {
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
  };

  const open = (target) => {
    if (typeof window === 'undefined') return;
    window.open(links[target], '_blank', 'noopener,noreferrer,width=640,height=560');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const btnClass = size === 'sm'
    ? 'h-8 w-8 p-0 rounded-full'
    : 'h-9 w-9 p-0 rounded-full';
  const iconClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="social-share">
      {label && <span className="text-xs font-medium text-slate-500">{label}</span>}

      <Button
        type="button"
        variant="outline"
        onClick={() => open('whatsapp')}
        className={`${btnClass} border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700`}
        title="Share on WhatsApp"
        data-testid="share-whatsapp"
        aria-label="Share on WhatsApp"
      >
        <svg viewBox="0 0 32 32" className={iconClass} fill="currentColor" aria-hidden="true">
          <path d="M16.003 3C9.375 3 4 8.373 4 15c0 2.114.552 4.176 1.6 5.994L4 29l8.19-2.147A11.94 11.94 0 0016.003 27C22.63 27 28 21.627 28 15S22.63 3 16.003 3zm0 21.727a9.72 9.72 0 01-4.949-1.36l-.354-.211-4.859 1.273 1.294-4.735-.231-.365A9.712 9.712 0 016.273 15c0-5.363 4.365-9.727 9.73-9.727S25.732 9.637 25.732 15 21.367 24.727 16.003 24.727zm5.317-7.286c-.29-.145-1.71-.844-1.975-.94-.265-.097-.458-.145-.65.145s-.746.94-.915 1.132c-.169.194-.338.218-.628.073-.29-.145-1.223-.451-2.328-1.438-.86-.767-1.44-1.716-1.61-2.006-.169-.29-.018-.446.127-.591.13-.13.29-.339.435-.508.145-.169.194-.29.29-.484.097-.194.048-.363-.024-.508-.073-.145-.65-1.57-.89-2.155-.235-.564-.475-.488-.65-.497l-.556-.01c-.194 0-.508.073-.774.363s-1.015.99-1.015 2.413 1.04 2.803 1.184 2.997c.145.194 2.046 3.124 4.958 4.379.694.3 1.234.478 1.657.612.696.222 1.329.19 1.83.115.559-.083 1.71-.699 1.951-1.373.242-.674.242-1.253.169-1.373-.073-.121-.265-.194-.556-.339z"/>
        </svg>
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={() => open('linkedin')}
        className={`${btnClass} border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700`}
        title="Share on LinkedIn"
        data-testid="share-linkedin"
        aria-label="Share on LinkedIn"
      >
        <svg viewBox="0 0 24 24" className={iconClass} fill="currentColor" aria-hidden="true">
          <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zM8.339 18.338V10.03H5.667v8.309h2.672zM7.003 8.878a1.549 1.549 0 100-3.098 1.549 1.549 0 000 3.098zm11.335 9.46v-4.554c0-2.475-1.322-3.626-3.084-3.626-1.42 0-2.058.783-2.415 1.331v-1.142H10.17c.036.752 0 8.309 0 8.309h2.669v-4.642c0-.238.017-.478.088-.649.194-.478.632-.973 1.37-.973.966 0 1.353.736 1.353 1.815v4.449h2.688z"/>
        </svg>
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={() => open('twitter')}
        className={`${btnClass} border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-900`}
        title="Share on X (Twitter)"
        data-testid="share-twitter"
        aria-label="Share on X"
      >
        <svg viewBox="0 0 24 24" className={iconClass} fill="currentColor" aria-hidden="true">
          <path d="M18.244 2H21.5l-7.44 8.505L22.75 22h-6.826l-5.35-6.997L4.5 22H1.242l7.964-9.104L1.25 2h6.998l4.838 6.4L18.244 2zm-1.196 18h1.834L7.02 3.9H5.05L17.048 20z"/>
        </svg>
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={copy}
        className={`${btnClass} border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700`}
        title="Copy link"
        data-testid="share-copy"
        aria-label="Copy link"
      >
        {copied ? <Check className={iconClass} /> : <Link2 className={iconClass} />}
      </Button>
    </div>
  );
};

export default SocialShare;
